import { merge } from 'lodash';
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts';

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart/base';
import { ChartBarProps } from '@/types/charts';
import { cn } from '@/utils/styles';

const ChartBar = ({ data, chartConfig, settings, className }: ChartBarProps) => {
  const seriesKeys = Object.keys(chartConfig);
  const { chart, grid, axes, labelList, legend } = settings;
  const { xAxis, yAxis } = axes ?? {};
  const { valuePrefix, valueSuffix, tooltipSubHeading, hideTooltipLabel, labelFormatter } = settings.tooltip ?? {};

  const seriesPropsWithColor = seriesKeys.reduce<Record<string, any>>(
    (acc, key) => ({
      ...acc,
      [key]: merge({}, settings.series, {
        dataKey: key,
        fill: `var(--color-${key})`,
      }),
    }),
    {},
  );

  return (
    <ChartContainer config={chartConfig} className={cn('min-h-[200px] w-full', className)}>
      <BarChart data={data} {...chart}>
        <CartesianGrid {...grid} />
        <XAxis
          {...xAxis}
          dataKey={chart?.layout === 'vertical' ? yAxis?.dataKey : xAxis?.dataKey}
          type={chart?.layout === 'vertical' ? 'number' : 'category'}
        />
        <YAxis
          {...yAxis}
          dataKey={chart?.layout === 'vertical' ? xAxis?.dataKey : yAxis?.dataKey}
          type={chart?.layout === 'vertical' ? 'category' : 'number'}
        />

        {!settings.tooltip?.hide && (
          <ChartTooltip
            cursor={settings.tooltip?.cursor}
            content={
              <ChartTooltipContent
                valuePrefix={valuePrefix}
                valueSuffix={valueSuffix}
                tooltipSubHeading={tooltipSubHeading}
                hideLabel={hideTooltipLabel}
                labelFormatter={labelFormatter}
              />
            }
          />
        )}

        {seriesKeys.map((key) => (
          <Bar key={key} {...seriesPropsWithColor[key]}>
            {!labelList?.hide && <LabelList dataKey={key} {...labelList} />}
          </Bar>
        ))}

        {!legend?.hide && <ChartLegend content={<ChartLegendContent textSize={legend?.textSize} />} />}
      </BarChart>
    </ChartContainer>
  );
};

export { ChartBar };
